export enum ValueType {
  encrypted = 'encrypted',
  string = 'string',
  number = 'number',
  image = 'image',
  video = 'video',
  audio = 'audio',
  file = 'file',
  color = 'color',
  date = 'date',
  dateTime = 'dateTime',
  insets = 'insets',
  boolean = 'boolean',
  url = 'url',
  list = 'list',
  user = 'user',
  record = 'record',
  array = 'array',
  coordinate = 'coordinate',
  calendarStyle = 'calendarStyle',
  calendarItemSelection = 'calendarItemSelection',
  accessLevel = 'accessLevel',
  richText = 'richText',
  json = 'json',
  keyValueMap = 'keyValueMap',
  vector = 'vector',
  duration = 'duration',
  currency = 'currency',
  offer = 'offer',
  resourceType = 'resourceType',
}

export type NumberConstant = string

export type ColorConstant = string

export type CurrencyConstant = string

export type DurationConstant = string

export type FontConstant = string

export interface ImageConstant {
  resourceId: string
}

export interface VideoConstant {
  resourceId: string
}

export interface AudioConstant {
  resourceId: string
}

export interface FileConstant {
  resourceId: string
}

export enum CalendarStyleConstant {
  monthlyHorizontal = 'monthlyHorizontal',
  monthlyVertical = 'monthlyVertical',
  weekly = 'weekly',
}

export enum CalendarItemSelectionConstant {
  noSelection = 'noSelection',
  rangeStart = 'rangeStart',
  rangeEnd = 'rangeEnd',
  rangeMiddle = 'rangeMiddle',
  single = 'single',
}

export enum ResourceTypeConstant {
  image = 'image',
  video = 'video',
  audio = 'audio',
  file = 'file',
}

export interface InsetsConstant {
  leading?: NumberConstant
  trailing?: NumberConstant
  top?: NumberConstant
  bottom?: NumberConstant
}

export interface CoordinateConstant {
  latitude?: NumberConstant
  longitude?: NumberConstant
}

export enum VariableSourceType {
  localVariable = 'localVariable',
  globalVariable = 'globalVariable',
  collection = 'collection',
  component = 'component',
  flow = 'flow',
  remoteConfig = 'remoteConfig',
}

export enum TableQueryFilterOperator {
  equal = 'equal',
  notEqual = 'notEqual',
  less = 'less',
  lessOrEqual = 'lessOrEqual',
  greater = 'greater',
  greaterOrEqual = 'greaterOrEqual',
  contains = 'contains',
  matches = 'matches',
  in = 'in',
  notIn = 'notIn',
}

export interface TableQueryFilter {
  field: string
  operator: TableQueryFilterOperator
  value: AnyVariable
}

export enum TableQueryOrder {
  ascending = 'ascending',
  descending = 'descending',
}

export interface FieldOrder {
  field: string
  order: TableQueryOrder
}

export interface TableQuery {
  filters?: TableQueryFilter[]
  ordered?: FieldOrder[]
}

export interface ItemSize {
  width?: NumberConstant
  height?: NumberConstant
}

export enum VariableTransformTransform {
  boolNot = 'boolNot',
  field = 'field',
  cast = 'cast',
  resize = 'resize',
  formatTimeInterval = 'formatTimeInterval',
  formatTimeAgo = 'formatTimeAgo',
  first = 'first',
  exists = 'exists',
  trimWhitespacesAndNewlines = 'trimWhitespacesAndNewlines',
  isEmpty = 'isEmpty',
  isNotEmpty = 'isNotEmpty',
  compareEqual = 'compareEqual',
  compareNotEqual = 'compareNotEqual',
  compareGreater = 'compareGreater',
  compareLess = 'compareLess',
  compareGreaterOrEqual = 'compareGreaterOrEqual',
  compareLessOrEqual = 'compareLessOrEqual',
  conditionalValue = 'conditionalValue',
  conversationImage = 'conversationImage',
  conversationTitle = 'conversationTitle',
  year = 'year',
  monthYearLong = 'monthYearLong',
  monthYearDateLong = 'monthYearDateLong',
  identifier = 'identifier',
  anyTrue = 'anyTrue',
  allTrue = 'allTrue',
  matchesRegexp = 'matchesRegexp',
  add = 'add',
  sub = 'sub',
  mult = 'mult',
  div = 'div',
  contains = 'contains',
  throttle = 'throttle',
  formatAsCurrency = 'formatAsCurrency',
  capitalizeFirstLetter = 'capitalizeFirstLetter',
  offer = 'offer',
  indexOf = 'indexOf',
  atIndex = 'atIndex',
  collectionParam = 'collectionParam',
  withField = 'withField',
}

export enum VariableTransformResizeType {
  proportionally = 'proportionally',
  exact = 'exact',
}

export interface VariableTransform {
  transform: VariableTransformTransform
  fieldName?: string
  paramName?: string
  query?: TableQuery
  value?: AnyVariable
  value2?: AnyVariable
  size?: ItemSize
  resizeType?: VariableTransformResizeType
  participantsKey?: string
  participantImageKey?: string
  participantNameKeys?: string[]
  type?: ValueType
}

export interface VariableSource {
  type: VariableSourceType
  variableName?: string
  componentName?: string
  collection?: CollectionVariable
  selector?: StringVariable
  fieldName?: string
  query?: TableQuery
  name?: string
  parameters?: InputParameter[]
  transforms?: VariableTransform[]
}

export interface BooleanVariable {
  constant?: boolean
  source?: VariableSource
}

export interface StringVariable {
  constant?: string
  source?: VariableSource
}

export interface NumberVariable {
  constant?: NumberConstant
  source?: VariableSource
}

export interface ColorVariable {
  constant?: ColorConstant
  source?: VariableSource
}

export interface ImageVariable {
  constant?: ImageConstant
  source?: VariableSource
}

export interface InsetsVariable {
  constant?: InsetsConstant
  source?: VariableSource
}

export interface CoordinateVariable {
  constant?: CoordinateConstant
  source?: VariableSource
}

export interface VideoVariable {
  constant?: VideoConstant
  source?: VariableSource
}

export interface AudioVariable {
  constant?: AudioConstant
  source?: VariableSource
}

export interface URLVariable {
  constant?: string
  source?: VariableSource
}

export interface DateVariable {
  constant?: string
  source?: VariableSource
}

export interface DateTimeVariable {
  constant?: string
  source?: VariableSource
}

export interface ListVariable {
  arrayConstant?: AnyVariable[]
  source?: VariableSource
}

export interface CalendarStyleVariable {
  constant?: CalendarStyleConstant
  source?: VariableSource
}

export interface CollectionVariable {
  name: string
  params?: {
    [key: string]: StringVariable
  }
  // for local
  collectionId?: string
}

export interface AnyVariable {
  urlConstant?: string
  accessLevelConstant?: string
  booleanConstant?: boolean
  dateConstant?: string
  dateTimeConstant?: string
  textConstant?: LocalizedContent
  stringConstant?: string
  imageConstant?: ImageConstant
  videoConstant?: VideoConstant
  audioConstant?: AudioConstant
  fileConstant?: FileConstant
  numberConstant?: NumberConstant
  colorConstant?: ColorConstant
  currencyConstant?: CurrencyConstant
  durationConstant?: DurationConstant
  insetsConstant?: InsetsConstant
  coordinateConstant?: CoordinateConstant
  calendarStyleConstant?: CalendarStyleConstant
  resourceTypeConstant?: ResourceTypeConstant
  arrayConstant?: AnyVariable[]
  source?: VariableSource
}

export enum ImageLayout {
  fill = 'fill',
  fit = 'fit',
  stretch = 'stretch',
  center = 'center',
}

export interface Action {
  options?: ActionOption
  enabled?: BooleanVariable
  actionType?: ActionType
  parameters?: InputParameter[]
  collection?: CollectionVariable
  record?: VariableSource
  recordModifications?: RecordModification[]
  componentName?: string
  variable?: AnyVariable
  valueTarget?: VariableSource
  transforms?: VariableTransform[]
  variableContextKey?: string
  description?: LocalizedContent
  subActions?: Action[]
  fallbackActions?: Action[]
  asynchronous?: boolean
}

export enum ActionType {
  placeholder = 'placeholder',
  check = 'check',
  checkAccessLevels = 'checkAccessLevels',
  checkNotificationsEnabled = 'checkNotificationsEnabled',
  checkLocationTrackingEnabled = 'checkLocationTrackingEnabled',
  checkIdfaAndCookieTrackingEnabled = 'checkIdfaAndCookieTrackingEnabled',
  appleAuthentication = 'appleAuthentication',
  googleAuthentication = 'googleAuthentication',
  anonymousAuthentication = 'anonymousAuthentication',
  createUserWithEmailAndPassword = 'createUserWithEmailAndPassword',
  signInWithEmailAndPassword = 'signInWithEmailAndPassword',
  sendPasswordResetEmail = 'sendPasswordResetEmail',
  sendSignInLinkToEmail = 'sendSignInLinkToEmail',
  reSendSignInLinkToEmail = 'reSendSignInLinkToEmail',
  enableNotifications = 'enableNotifications',
  enableIdfaAndCookieTracking = 'enableIdfaAndCookieTracking',
  enableLocationTracking = 'enableLocationTracking',
  logout = 'logout',
  openEmail = 'openEmail',
  openWeb = 'openWeb',
  openLink = 'openLink',
  checkoutSelected = 'checkoutSelected',
  restorePurchases = 'restorePurchases',
  deleteAccount = 'deleteAccount',
  updateRecord = 'updateRecord',
  createRecord = 'createRecord',
  deleteRecord = 'deleteRecord',
  setValue = 'setValue',
  clearValue = 'clearValue',
  getImage = 'getImage',
  getVideo = 'getVideo',
  getAudio = 'getAudio',
  getFile = 'getFile',
  navigate = 'navigate',
  navigateBack = 'navigateBack',
  show = 'show',
  openDialog = 'openDialog',
  closeDialog = 'closeDialog',
  closeAllDialogs = 'closeAllDialogs',
  scrollToStart = 'scrollToStart',
  scrollToEnd = 'scrollToEnd',
  subscribe = 'subscribe',
  openDocument = 'openDocument',
  runFlow = 'runFlow',
  handleDeeplink = 'handleDeeplink',
  oauth = 'oauth',
}

export interface RecordModification {
  type: 'set'
  fieldName?: string
  value?: AnyVariable
}

export interface InputParameter {
  name?: string
  value?: AnyVariable
}

export interface ActionOption {
  email?: string
  screen?: string
  dialog?: string
  name?: string
  link?: URLVariable
}

export interface LocalizedContent {
  key: string
  locales: { [key: string]: string }
  values?: VariableSource[]
}

export enum TextAlignment {
  leading = 'leading',
  trailing = 'trailing',
  center = 'center',
}

export interface TabBar {
  id: string
  name: string
  backgroundColor?: ColorVariable
  tabs?: TabBarItem[]
}

export interface TabBarItem {
  screen: string
  title?: LocalizedContent
  image?: ImageVariable
  visible?: BooleanVariable
}

export interface ScreenParameter {
  parameter: string
  required?: boolean
  inout?: boolean
  type: ValueType
  accept?: ValueType
  dataObject?: string
  collection?: string
}

// for local
export enum BarComponentType {
  tabBar = 'tabBar',
  tab = 'tab',
  topBar = 'topBar',
}

export enum ComponentType {
  view = 'view',
  spacer = 'spacer',
  divider = 'divider',
  stack = 'stack',
  overlay = 'overlay',
  carousel = 'carousel',
  datePicker = 'datePicker',
  button = 'button',
  image = 'image',
  text = 'text',
  textInput = 'textInput',
  video = 'video',
  list = 'list',
  scroll = 'scroll',
  map = 'map',
  offer = 'offer',
  themePicker = 'themePicker',
  notificationsSettings = 'notificationsSettings',
  calendar = 'calendar',
  videoPlayer = 'videoPlayer',
  audioPlayer = 'audioPlayer',
  slider = 'slider',
  toggle = 'toggle',
  progressIndicator = 'progressIndicator',
}

export enum ComponentAlignment {
  fill = 'fill',
  leading = 'leading',
  start = 'start',
  top = 'top',
  topLeading = 'topLeading',
  topCenter = 'topCenter',
  topTrailing = 'topTrailing',
  centerLeading = 'centerLeading',
  center = 'center',
  centerTrailing = 'centerTrailing',
  trailing = 'trailing',
  end = 'end',
  bottom = 'bottom',
  bottomLeading = 'bottomLeading',
  bottomCenter = 'bottomCenter',
  bottomTrailing = 'bottomTrailing',
}

export enum ComponentsDirection {
  horizontal = 'horizontal',
  vertical = 'vertical',
  all = 'all',
}

export enum TextInputType {
  general = 'general',
  email = 'email',
  password = 'password',
  number = 'number',
  phone = 'phone',
}

export enum ImageLocation {
  leading = 'leading',
  trailing = 'trailing',
  leadingAttached = 'leadingAttached',
  trailingAttached = 'trailingAttached',
  top = 'top',
  bottom = 'bottom',
}

export enum TextType {
  plain = 'plain',
  markdown = 'markdown',
}

export enum ListStyle {
  regular = 'regular',
  gallery = 'gallery',
  grid = 'grid',
}

export enum MapStyle {
  standard = 'standard',
  hybrid = 'hybrid',
  satellite = 'satellite',
}

export interface ScreenComponent {
  id: string
  componentType: ComponentType
  name: string
  visible?: BooleanVariable
  enabled?: BooleanVariable
  alpha?: NumberVariable
  cornersRadius?: NumberVariable
  borderColor?: ColorVariable
  borderWidth?: NumberVariable
  width?: NumberVariable
  height?: NumberVariable
  widthHeightRatio?: NumberVariable
  margins?: InsetsConstant
  backgroundColor?: ColorVariable
  backgroundPattern?: ImageConstant
  backgroundGradient?: BackgroundGradient
  actions?: Action[]
  localVariables?: LocalVariable[]
  componentAlignment: ComponentAlignment
  subComponents?: ScreenComponent[]
  componentsSpacing?: NumberVariable
  removeHiddenComponents?: BooleanVariable
  componentsDirection?: ComponentsDirection
  text?: LocalizedContent
  textAlignment?: TextAlignment
  textColor?: ColorVariable
  font?: FontConstant
  numberOfLines?: NumberVariable
  textType?: TextType
  showMoreTextButton?: BooleanVariable
  textInputType?: TextInputType
  textInputMultiline?: BooleanVariable
  placeholderText?: LocalizedContent
  placeholderTextColor?: ColorVariable
  placeholderTextFont?: FontConstant
  contentPadding?: InsetsVariable
  image?: ImageVariable
  imageColor?: ColorVariable
  imageLayout?: ImageLayout
  imageLocation?: ImageLocation
  placeholderImage?: ImageVariable
  placeholderImageColor?: ColorVariable
  placeholderImageLayout?: ImageLayout
  date?: DateVariable
  minimumDate?: DateVariable
  maximumDate?: DateVariable
  dateFormat?: DateTimeFormat
  displayDate?: DateVariable
  video?: VideoVariable
  audio?: AudioVariable
  autoplay?: BooleanVariable
  loop?: BooleanVariable
  duration?: NumberVariable
  showScrollBar?: BooleanVariable
  reversed?: BooleanVariable
  listItemContextKey?: string
  listItems?: ListVariable
  listStyle?: ListStyle
  visibleItem?: AnyVariable
  minWidth?: NumberVariable
  minHeight?: NumberVariable
  listItemCoordinate?: CoordinateVariable
  listItemSectionKey?: AnyVariable
  mapStyle?: MapStyle
  showUserLocation?: BooleanVariable
  coordinate?: CoordinateVariable
  zoom?: NumberVariable
  offer?: VariableSource
  selected?: BooleanVariable
  calendarStyle?: CalendarStyleVariable
  showHeader?: BooleanVariable
  headerFont?: FontConstant
  headerColor?: ColorVariable
  showWeekdays?: BooleanVariable
  listItemDate?: DateVariable
  value?: NumberVariable
  minimumValue?: NumberVariable
  maximumValue?: NumberVariable
  primaryColor?: ColorVariable
  secondaryColor?: ColorVariable
  discrete?: BooleanVariable
  vbState?: string
  // for local
  listId?: string
  indexInList?: number
}

export enum ScreenSize {
  responsive = 'responsive',
  compact = 'compact',
}

export enum Module {
  launchScreen = 'launchScreen',
  onboarding = 'onboarding',
  beforeJoin = 'beforeJoin',
  authentication = 'authentication',
  emailAuthentication = 'emailAuthentication',
  permissions = 'permissions',
  home = 'home',
  profile = 'profile',
  about = 'about',
  settings = 'settings',
  video = 'video',
  paywall = 'paywall',
  others = 'others',
}

export enum ScreenStatusBarStyle {
  default = 'default',
  inverted = 'inverted',
  light = 'light',
  dark = 'dark',
  hidden = 'hidden',
}

export enum ScreenTopBarComponentsKey {
  leadingComponents = 'leadingComponents',
  headlineComponents = 'headlineComponents',
  trailingComponents = 'trailingComponents',
}

export interface ScreenTopBar {
  backgroundColor?: ColorVariable
  [ScreenTopBarComponentsKey.leadingComponents]?: ScreenComponent[]
  [ScreenTopBarComponentsKey.headlineComponents]?: ScreenComponent[]
  [ScreenTopBarComponentsKey.trailingComponents]?: ScreenComponent[]
}

export interface ExperimentalPane {
  screenName?: string
  screenComponentIds?: string[]
  components?: ScreenComponent[]
}

export interface Experimental {
  trailingPane?: ExperimentalPane
  leadingPane?: ExperimentalPane
}

export interface Screen {
  path: string
  id: string
  screenName: string
  size: ScreenSize
  module: Module
  components?: ScreenComponent[]
  enabled?: BooleanVariable
  onScreenVisitActions?: Action[]
  statusBarStyle?: ScreenStatusBarStyle
  backgroundColor?: ColorVariable
  backgroundPattern?: ImageConstant
  backgroundGradient?: BackgroundGradient
  dialog?: boolean
  topBar?: ScreenTopBar
  showTopBar?: BooleanVariable
  showBackButton?: BooleanVariable
  screenTitle?: LocalizedContent
  inputParameters?: ScreenParameter[]
  localVariables?: LocalVariable[]
  experimental?: Experimental
}

export enum DateTimeFormatFormat {
  short = 'short',
  medium = 'medium',
  full = 'full',
}

export interface DateTimeFormat {
  format?: DateTimeFormatFormat
  customFormat?: string
}

export interface IosConfig {
  bundleId: string
  teamId?: string
  appGroups?: string[]
  appleKeyId?: string
  projectName?: string
}

export interface AndroidConfig {
  packageName: string
  projectName?: string
}

export interface WebConfig {
  projectName?: string
}

export interface BackendConfig {
  projectName?: string
}

export interface Resource {
  id: string
  assetId?: string
  version?: string
  format: string
  folder?: string
  bytes: number
  width: number
  height: number
  signature?: string
  url: string
  resourceType: ResourceTypeConstant
  createdAt: string
  name: string
}

export enum BackgroundGradientDirection {
  vertical = 'vertical',
  horizontal = 'horizontal',
  radial = 'radial',
}

export interface BackgroundGradient {
  start: ColorConstant
  end: ColorConstant
  direction: BackgroundGradientDirection
}

export enum BrandingColorType {
  _primary = '_primary',
  _secondary = '_secondary',
  _tertiary = '_tertiary',
  _neutral = '_neutral',
  _error = '_error',
  primary = 'primary',
  onPrimary = 'onPrimary',
  primaryContainer = 'primaryContainer',
  onPrimaryContainer = 'onPrimaryContainer',
  primaryFixed = 'primaryFixed',
  onPrimaryFixed = 'onPrimaryFixed',
  primaryFixedDim = 'primaryFixedDim',
  onPrimaryFixedVariant = 'onPrimaryFixedVariant',
  inversePrimary = 'inversePrimary',
  secondary = 'secondary',
  onSecondary = 'onSecondary',
  secondaryContainer = 'secondaryContainer',
  onSecondaryContainer = 'onSecondaryContainer',
  secondaryFixed = 'secondaryFixed',
  onSecondaryFixed = 'onSecondaryFixed',
  secondaryFixedDim = 'secondaryFixedDim',
  onSecondaryFixedVariant = 'onSecondaryFixedVariant',
  tertiary = 'tertiary',
  onTertiary = 'onTertiary',
  tertiaryContainer = 'tertiaryContainer',
  onTertiaryContainer = 'onTertiaryContainer',
  tertiaryFixed = 'tertiaryFixed',
  onTertiaryFixed = 'onTertiaryFixed',
  tertiaryFixedDim = 'tertiaryFixedDim',
  onTertiaryFixedVariant = 'onTertiaryFixedVariant',
  background = 'background',
  onBackground = 'onBackground',
  surface = 'surface',
  onSurface = 'onSurface',
  surfaceVariant = 'surfaceVariant',
  onSurfaceVariant = 'onSurfaceVariant',
  surfaceDim = 'surfaceDim',
  surfaceBright = 'surfaceBright',
  surfaceContainerLowest = 'surfaceContainerLowest',
  surfaceContainerLow = 'surfaceContainerLow',
  surfaceContainer = 'surfaceContainer',
  surfaceContainerHigh = 'surfaceContainerHigh',
  surfaceContainerHighest = 'surfaceContainerHighest',
  inverseSurface = 'inverseSurface',
  inverseOnSurface = 'inverseOnSurface',
  outline = 'outline',
  outlineVariant = 'outlineVariant',
  shadow = 'shadow',
  scrim = 'scrim',
  error = 'error',
  onError = 'onError',
  errorContainer = 'errorContainer',
  onErrorContainer = 'onErrorContainer',
  transparent = 'transparent',
  semiTransparentBackground = 'semiTransparentBackground',
  white = 'white',
  black = 'black',
}

export enum BrandingThemeType {
  light = 'light',
  dark = 'dark',
}

export type BrandingColorStyles = {
  [key in BrandingColorType]: { [key in BrandingThemeType]: string }
}

export enum Platform {
  iOS = 'iOS',
  Android = 'Android',
}

export interface BrandingFontStyleItem {
  fontFamily: string
  fontStyle: string
  fontWeight: number
  fontSize: number
  lineHeight: number
  letterSpacing: number
}

export interface BrandingFontStyle {
  styleName: string
  categoryName: string
  ios: BrandingFontStyleItem
  android: BrandingFontStyleItem
}

export interface BrandingConstants {
  buttonHeight: number
  cornersRadius: number
  fieldHeight: number
  horizontalMargin: number
  horizontalSpacing: number
  thinSeparator: number
  verticalMargin: number
  verticalSpacing: number
}

export enum InnerLaunchScreenIcon {
  splashIcon = 'splashIcon',
  brandLogo = 'brandLogo',
}

export enum LaunchScreenIcon {
  androidIcon = 'androidIcon',
  iosIcon = 'iosIcon',
}

export interface CollectionProperty {
  name: string
  type: ValueType
  position: number
  isRequired?: boolean
  isSystem?: boolean
  isHidden?: boolean
  accept?: ValueType
  dataObject?: string
  collection?: string
  helpText?: string
  acceptValues?: string[]
}

export interface CollectionPermissions {
  list: string
  get: string
  create: string
  update: string
  delete: string
}

export interface CollectionDatasource {
  type: 'firestore'
  databaseName?: string
  collectionName: string
}

export interface Collection {
  name: string
  singular: string
  properties?: CollectionProperty[]
  datasource: CollectionDatasource
  isSystem?: boolean
  helpText?: string
  titleField?: string
  previewScreen?: string
  onDocumentCreated?: string
  onDocumentUpdated?: string
  onDocumentDeleted?: string
  onDocumentWritten?: string
  permissions: CollectionPermissions
}

export interface Storage {
  name: string
  datasource: {
    type: string
    bucketName: string
  }
  onObjectArchived?: string
  onObjectDeleted?: string
  onObjectFinalized?: string
}

export interface DataObject {
  name: string
  properties?: CollectionProperty[]
}

export interface LocalVariable {
  name: string
  variable?: AnyVariable
  type?: ValueType
  accept?: ValueType
  dataObject?: string
  collection?: string
}

export interface GlobalVariable {
  variableName: string
  variable: AnyVariable
}

export interface Flow {
  id: string
  name: string
  input: DataObject
  output: DataObject
  sourceCode: string
}

export interface Prompt {
  id: string
  name: string
  input: DataObject
  output: DataObject
  sourceCode: string
}

export interface Indexer {
  id: string
  name: string
  input: DataObject
  output: DataObject
  sourceCode: string
}

export interface Retriever {
  id: string
  name: string
  input: DataObject
  output: DataObject
  sourceCode: string
}

export interface Embedder {
  id: string
  name: string
  input: DataObject
  output: DataObject
  sourceCode: string
}

export interface File {
  id: string
  name: string
  sourceCode: string
}

export interface Tool {
  id: string
  name: string
  input: DataObject
  output: DataObject
  sourceCode: string
}

export interface Rule {
  id: string
  name: string
  sourceCode: string
}

export interface ConfigLogic {
  flows?: Flow[]
  prompts?: Prompt[]
  indexers?: Indexer[]
  retrievers?: Retriever[]
  embedders?: Embedder[]
  files?: File[]
  tools?: Tool[]
  rules?: Rule[]
}

export interface AssetsBindings {
  collections: {
    assets: string
  }
  triggers?: {
    onTranscoderStatus?: string
  }
}

export interface AuthenticationBindings {
  collections: {
    devices: string
  }
  triggers: {
    beforeUserCreated?: string
    beforeUserSignedIn?: string
    onUserCreated?: string
    onUserDeleted?: string
    onUserSignedOut: string
    onPushToken: string
  }
  type: 'firebase-auth'
}

export interface BillingBindings {
  collections: {
    offers: string
    products: string
  }
  triggers: {
    onAppStoreNotification: string
    onAppStoreReceipts: string
    onGooglePlayNotification: string
    onGooglePlayReceipts: string
    onStripeCheckout: string
    onStripeCheckoutEnded: string
    onStripeNotification: string
  }
}

export interface RemoteConfig {
  apiUrl?: string
  oAuthClientId?: string
  variables?: RemoteConfigVariable[]
}

export interface RemoteConfigVariable {
  type: ValueType
  source: 'firebase'
  name: string
}

export interface Data {
  collections?: Collection[]
  dataObjects?: DataObject[]
  globalVariables?: GlobalVariable[]
  storages?: Storage[]
  remoteConfig?: RemoteConfig
}

export interface Config {
  projectName: string
  platforms: {
    ios: IosConfig
    android: AndroidConfig
    web?: WebConfig
    backend?: BackendConfig
  }
  assets: AssetsBindings
  authentication: AuthenticationBindings
  billing?: BillingBindings
  screens?: Screen[]
  data: Data
  domains?: string[]
  logic: ConfigLogic
  codeplatform: {
    syntaxVersion: string
  }
  preview: string
  resources?: Resource[]
  localization: {
    languages?: string[]
    primaryLanguage: string
  }
  branding: {
    appName: LocalizedContent
    icons: { [key in LaunchScreenIcon]: string }
    typography: {
      iosDefaultFontFamily: string
      androidDefaultFontFamily: string
      customFontFamilies?: string[]
      fontStyles?: BrandingFontStyle[]
    }
    colorStyles: BrandingColorStyles
    constants: BrandingConstants
  }
  tabBars?: TabBar[]
}
